// GA4 event catalog — the client↔GTM contract. One function per event; call
// sites cannot compile an illegal payload. Event names, parameters, and
// enums follow the analytics spec ("GA vs BQ: Lean In Connect — GA4 Events
// v6") as corrected by docs/superpowers/specs/2026-07-20-ga4-datalayer-
// analytics-design.md — the corrections table there is authoritative where
// the spreadsheet and the codebase disagree.
//
// Firing rules (see the design doc): success-only (never onSettled), and
// every push goes through pushDL. BQ-only parameters are pushed like all
// others; whether GTM maps a parameter to a GA4 dimension is container
// config, not client code.
import type { ResourceKind } from '@leanin/shared/resource-kinds';

import type { EntryJourney } from '../entry-journey';
import { pushDL } from './data-layer';
import type { PageType } from './page-meta';

export type ContentSection = 'circle' | 'network' | 'space' | 'global';
export type GroupType = 'circle' | 'network' | 'group';
export type GroupKind = 'circle' | 'network' | 'space';
export type ContactType = 'direct' | 'group';
export type OnboardingRole =
  | 'lean-in'
  | 'circle-member'
  | 'circle-leader'
  | 'network-leader';
export type FunnelType = 'registration' | 'checklist';
export type AuthMethod = 'email' | 'sso';
export type ShareEntityType = 'post' | 'resource' | 'event' | 'profile';

// groups.kind 'space' is surfaced to members as "group"; analytics keeps
// the member-facing vocabulary.
export function groupKindToType(kind: GroupKind): GroupType {
  return kind === 'space' ? 'group' : kind;
}

const PERSONA_TO_ROLE: Record<'li' | 'cm' | 'cl' | 'nl', OnboardingRole> = {
  li: 'lean-in',
  cm: 'circle-member',
  cl: 'circle-leader',
  nl: 'network-leader',
};

export function personaToRole(
  persona: 'li' | 'cm' | 'cl' | 'nl',
): OnboardingRole {
  return PERSONA_TO_ROLE[persona];
}

// Post visibility is two orthogonal axes (isGlobal + scopes[]); a single
// content_section is deliberately lossy: global wins, else the first
// scope's kind is treated as primary. See the design doc's corrections
// table before changing this.
export function postContentSection(post: {
  isGlobal: boolean;
  scopes: ReadonlyArray<{ groupKind: GroupKind }>;
}): ContentSection {
  const primary = post.scopes[0];
  if (post.isGlobal || primary === undefined) return 'global';
  return primary.groupKind;
}

// has_link has no backing field (no 'link' attachment kind exists) — it is
// derived from the body text.
const URL_PATTERN = /(?:https?:\/\/|www\.)\S+\.\S{2,}/i;

export function bodyHasUrl(body: string): boolean {
  return URL_PATTERN.test(body);
}

// ── Ad entry journeys ────────────────────────────────────────────────────

export function trackEntryJourneyViewed(params: {
  journey: EntryJourney;
}): void {
  pushDL({ event: 'entry_journey_viewed', ...params });
}

export function trackEntryJourneyCtaClicked(params: {
  journey: EntryJourney;
}): void {
  pushDL({ event: 'entry_journey_cta_clicked', ...params });
}

// ── Auth & onboarding ────────────────────────────────────────────────────

// user_id is the internal users-table id (see data-layer.ts), read from the
// freshly refetched session at the call site so the id rides the same push
// GTM triggers on. Null means the session read failed — the key is omitted
// (not pushed as null) so the push can't clear the data-model user_id that
// user_id_set maintains.
export function trackSignUp(params: {
  method: AuthMethod;
  user_id: string | null;
}): void {
  pushDL({
    event: 'sign_up',
    method: params.method,
    ...(params.user_id !== null && { user_id: params.user_id }),
  });
}

export function trackLogin(params: {
  method: AuthMethod;
  user_id: string | null;
}): void {
  pushDL({
    event: 'login',
    method: params.method,
    ...(params.user_id !== null && { user_id: params.user_id }),
  });
}

export type OnboardingStepParams = {
  step: string;
  role: OnboardingRole;
  step_number: number;
  total_steps: number;
  funnel_type: FunnelType;
};

export function trackOnboardingStepViewed(params: OnboardingStepParams): void {
  pushDL({ event: 'onboarding_step_viewed', ...params });
}

export function trackOnboardingStepCompleted(
  params: OnboardingStepParams,
): void {
  pushDL({ event: 'onboarding_step_completed', ...params });
}

export function trackOnboardingCompleted(params: {
  role: OnboardingRole;
  funnel_type: FunnelType;
}): void {
  pushDL({ event: 'onboarding_completed', ...params });
}

// invite_source is intentionally absent — no backing field exists on any
// invite response. role_granted is BQ-only.
export function trackInviteAccepted(params: {
  invite_type: GroupKind;
  role_granted: string;
}): void {
  pushDL({ event: 'invite_accepted', ...params });
}

export function trackProfilePhotoUploaded(params: {
  photo_type: 'profile' | 'cover';
}): void {
  pushDL({ event: 'profile_photo_uploaded', ...params });
}

// ── Profile & settings ───────────────────────────────────────────────────

export function trackProfileUpdated(params: {
  fields_changed: string[];
}): void {
  pushDL({ event: 'profile_updated', ...params });
}

// Product consent purposes only (PrivacyClient keys mapped through
// PRIVACY_KEY_TO_PURPOSE) — cookie-banner consent flows through
// CookieYes/Consent Mode, never this event.
export function trackConsentChanged(params: {
  consent_type: string;
  new_value: boolean;
}): void {
  pushDL({ event: 'consent_changed', ...params });
}

// ── Feed & posts ─────────────────────────────────────────────────────────

export function trackPostCreated(params: {
  content_section: ContentSection;
  has_media: boolean;
  has_link: boolean;
  mention_count: number;
}): void {
  pushDL({ event: 'post_created', ...params });
}

export function trackPostReacted(params: {
  reaction_type: string;
  content_section: ContentSection;
}): void {
  pushDL({ event: 'post_reacted', ...params });
}

export function trackPostCommented(params: {
  content_section: ContentSection;
  has_mention: boolean;
}): void {
  pushDL({ event: 'post_commented', ...params });
}

export function trackPostSaved(params: {
  content_section: ContentSection;
}): void {
  pushDL({ event: 'post_saved', ...params });
}

export function trackPostPinned(params: {
  content_section: ContentSection;
}): void {
  pushDL({ event: 'post_pinned', ...params });
}

export function trackContentSharedToGroup(params: {
  entity_type: ShareEntityType;
  share_destination_type: string;
}): void {
  pushDL({ event: 'content_shared_to_group', ...params });
}

export function trackShareLinkCopied(params: {
  entity_type: ShareEntityType;
}): void {
  pushDL({ event: 'share_link_copied', ...params });
}

// ── Circles, networks & groups ───────────────────────────────────────────

export function trackGroupCreated(params: {
  group_type: GroupType;
  access?: string;
  join_policy?: string;
}): void {
  pushDL({ event: 'group_created', ...params });
}

// 'cta' = an in-product join button; 'post_signup' = the automatic join
// /auth/finishing performs from a join link or a pre-signup join intent.
// The split is what separates ad/link-driven joins from organic ones.
export type JoinMethod = 'cta' | 'post_signup';

export function trackGroupJoined(params: {
  group_type: GroupType;
  role_on_join: string;
  join_method: JoinMethod;
}): void {
  pushDL({ event: 'group_joined', ...params });
}

export function trackGroupLeft(params: { group_type: GroupType }): void {
  pushDL({ event: 'group_left', ...params });
}

export function trackGroupViewed(params: {
  group_type: GroupType;
  is_member: boolean;
  is_leader: boolean;
}): void {
  pushDL({ event: 'group_viewed', ...params });
}

export function trackMemberInvited(params: {
  group_type: GroupType;
  invite_count: number;
  has_note: boolean;
}): void {
  pushDL({ event: 'member_invited', ...params });
}

export function trackMemberRoleChanged(params: {
  group_type: GroupType;
  new_role: string;
}): void {
  pushDL({ event: 'member_role_changed', ...params });
}

export function trackBroadcastCreated(params: {
  group_type: GroupType;
  is_scheduled: boolean;
}): void {
  pushDL({ event: 'broadcast_created', ...params });
}

// Platform-admin broadcast send. The spreadsheet's group_type +
// immediate|scheduled vocabulary matches no real field on this feature —
// the actual audience/channel unions are pushed instead (flagged in the
// design doc's corrections table).
export type BroadcastAudienceType =
  | 'all'
  | 'circles'
  | 'networks'
  | 'spaces'
  | 'members'
  | 'role'
  | 'recent_active';

export function trackBroadcastSent(params: {
  audience_type: BroadcastAudienceType;
  send_via: 'email' | 'email_in_app';
}): void {
  pushDL({ event: 'broadcast_sent', ...params });
}

export function trackNetworkLeaderApplicationSubmitted(): void {
  pushDL({ event: 'network_leader_application_submitted' });
}

// ── Calendar events ──────────────────────────────────────────────────────

export function trackEventCreated(params: {
  content_section: ContentSection;
  is_virtual: boolean;
}): void {
  pushDL({ event: 'event_created', ...params });
}

export function trackEventViewed(params: { is_rsvped: boolean }): void {
  pushDL({ event: 'event_viewed', ...params });
}

export function trackEventRsvped(params: {
  rsvp_status: 'going' | 'maybe' | 'declined';
}): void {
  pushDL({ event: 'event_rsvped', ...params });
}

// ── Messages ─────────────────────────────────────────────────────────────

export function trackMessageSent(params: { contact_type: ContactType }): void {
  pushDL({ event: 'message_sent', ...params });
}

export function trackDirectConversationCreated(): void {
  pushDL({ event: 'direct_conversation_created' });
}

export function trackConversationRead(params: {
  contact_type: ContactType;
}): void {
  pushDL({ event: 'conversation_read', ...params });
}

// ── Resources & learning ─────────────────────────────────────────────────

export function trackResourceViewed(params: {
  content_type: ResourceKind;
}): void {
  pushDL({ event: 'resource_viewed', ...params });
}

export function trackResourceCreated(params: {
  content_type: ResourceKind;
}): void {
  pushDL({ event: 'resource_created', ...params });
}

export function trackResourceSaved(params: {
  content_type: ResourceKind;
}): void {
  pushDL({ event: 'resource_saved', ...params });
}

export function trackResourceFileDownloaded(params: {
  file_extension: string;
}): void {
  pushDL({ event: 'resource_file_downloaded', ...params });
}

export function trackLearningTrackCreated(params: {
  resource_count: number;
}): void {
  pushDL({ event: 'learning_track_created', ...params });
}

// ── Search & discovery ───────────────────────────────────────────────────

// search_term is intentionally absent (legal hold — see the design doc);
// query_length is the BQ-only stand-in. results_count_first_page is the
// loaded first-page hit count — no true total exists on any surface.
export function trackSearch(params: {
  search_location: string;
  query_length: number;
  results_count_first_page: number;
}): void {
  pushDL({ event: 'search', ...params });
}

export function trackSearchResultClicked(params: {
  content_type: string;
  result_position: number;
}): void {
  pushDL({ event: 'search_result_clicked', ...params });
}

// ── Social & navigation ──────────────────────────────────────────────────

// user_type is held for both follow + profile-view events: the spec's
// admin|moderator|member enum has no backing field (platformRole has
// different semantics). Pending an analytics definition.
export function trackMemberFollowed(): void {
  pushDL({ event: 'member_followed' });
}

export function trackProfileViewed(params: {
  is_own_profile: boolean;
}): void {
  pushDL({ event: 'profile_viewed', ...params });
}

export function trackPageViewed(params: {
  page_type: PageType;
  page_id: string | null;
}): void {
  pushDL({ event: 'page_viewed', ...params });
}

export function trackNotificationClicked(params: {
  notification_type: string;
}): void {
  pushDL({ event: 'notification_clicked', ...params });
}
